// Fuel-tank Excel import dialog — a guided, transparent import for the Fuel Tank — S/N page.
// Pick the warehouse "Fuel Tank Serial Number" workbook; it's parsed in the browser and every
// row that names a vessel (Σκάφος) is matched to an overview vessel by SAP or name. The dialog
// shows exactly what will be written and what will be skipped (and why) before anything is saved.
// On phones the preview is one stats line and a plain list, with no inner scrollers: only the sheet body
// scrolls, and the pinned footer keeps "Import N tanks" in reach.

import { useCallback, useMemo, useRef, useState } from 'react';
import { InlineError, LoadingState, Modal, Pill, Tile } from './ui.js';
import {
  parseFuelTankWorkbook,
  planImport,
  type ExistingTankLite,
  type ImportPlan,
  type OverviewVesselLite,
  type ParsedSheet,
} from '../features/fuelTankImport.js';
import { IconCheck } from './icons.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';

interface ImportItem {
  sap: string;
  serial: string;
  receiptDate: string;
  floatInstalled: string;
  installDate: string;
  note: string;
}

export function FuelTankImportModal({
  vessels,
  existingBySap,
  seasons,
  activeSeason,
  fetchSeasonVessels,
  onClose,
  onConfirm,
}: {
  vessels: OverviewVesselLite[];
  existingBySap: Map<string, ExistingTankLite[]>;
  seasons: string[];
  activeSeason: string;
  /** load another season's vessels so unmatched rows can be matched against it. */
  fetchSeasonVessels: (seasonId: string) => Promise<OverviewVesselLite[]>;
  onClose: () => void;
  /** persist the mapped tanks; resolves with the number stored. */
  onConfirm: (items: ImportItem[]) => Promise<number>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const isPhone = useIsPhone();
  const df = useDateFormatter();
  const coarse = useIsCoarse(); // a touch screen can't drop a file, so the picker copy only says "choose"
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);
  // unmatched handling: the parsed workbook, the accumulated match pool, and the "move to another
  // season" state so unmatched vessels can be resolved against a different season's overview.
  const [sheets, setSheets] = useState<ParsedSheet[] | null>(null);
  const [pool, setPool] = useState<OverviewVesselLite[]>(vessels);
  const [addedSeasons, setAddedSeasons] = useState<string[]>([]);
  const [scrapped, setScrapped] = useState(false);
  const [moveSeason, setMoveSeason] = useState('');
  const [rechecking, setRechecking] = useState(false);
  const [moveMsg, setMoveMsg] = useState('');

  const readFile = useCallback(
    async (file: File) => {
      setParsing(true);
      setParseError(null);
      setSaveError(null);
      setPlan(null);
      setDone(null);
      setFileName(file.name);
      setSheets(null);
      setPool(vessels);
      setAddedSeasons([]);
      setScrapped(false);
      setMoveSeason('');
      setMoveMsg('');
      try {
        const XLSX: unknown = await import('xlsx');
        const parsed = parseFuelTankWorkbook(XLSX, await file.arrayBuffer());
        if (parsed.length === 0) {
          setParseError('No fuel-tank sheets found. Expected sheets with an “S/N” column (GT7, GTX, A50…).');
          return;
        }
        setSheets(parsed);
        setPlan(planImport(parsed, vessels, existingBySap));
      } catch (e) {
        setParseError(e instanceof Error ? e.message : 'Could not read this file.');
      } finally {
        setParsing(false);
      }
    },
    [vessels, existingBySap],
  );

  const otherSeasons = useMemo(
    () => seasons.filter((s) => s !== activeSeason && !addedSeasons.includes(s)),
    [seasons, activeSeason, addedSeasons],
  );

  // "move to another season": pull that season's vessels into the match pool and re-plan, so
  // previously-unmatched rows resolve to their real SAP and get imported (stored per-SAP).
  const recheckSeason = async () => {
    if (!sheets || !moveSeason) return;
    setRechecking(true);
    setMoveMsg('');
    try {
      const extra = await fetchSeasonVessels(moveSeason);
      const before = plan?.stats.unmatched ?? 0;
      const nextPool = [...pool, ...extra];
      const nextPlan = planImport(sheets, nextPool, existingBySap);
      const gained = before - nextPlan.stats.unmatched;
      setPool(nextPool);
      setAddedSeasons((a) => [...a, moveSeason]);
      setPlan(nextPlan);
      setMoveMsg(
        gained > 0
          ? `Matched ${gained} more from Season ${moveSeason}. They will be saved to those vessels (switch to Season ${moveSeason} to see them).`
          : `No matches found in Season ${moveSeason}.`,
      );
      setMoveSeason('');
    } catch {
      setMoveMsg('Could not load that season.');
    } finally {
      setRechecking(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) void readFile(f);
  };

  const confirm = async () => {
    if (!plan || plan.imports.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const count = await onConfirm(
        plan.imports.map((i) => ({
          sap: i.sap,
          serial: i.serial,
          receiptDate: i.receiptDate,
          floatInstalled: i.floatInstalled,
          installDate: i.installDate,
          note: i.note,
        })),
      );
      setDone(count);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setSaving(false);
    }
  };

  const imports = useMemo(
    () => (plan ? [...plan.imports].sort((a, b) => a.vesselName.localeCompare(b.vesselName) || a.tankNo - b.tankNo) : []),
    [plan],
  );
  const unmatched = plan?.skipped.filter((s) => s.reason === 'unmatched') ?? [];
  const duplicates = plan?.skipped.filter((s) => s.reason === 'duplicate') ?? [];
  const overflow = plan?.skipped.filter((s) => s.reason === 'overflow') ?? [];

  const cellSm: React.CSSProperties = { fontSize: 11, padding: '3px 6px' };

  return (
    <Modal
      title="Import fuel tanks from Excel"
      width={880}
      onClose={onClose}
      footer={
        done != null ? (
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            {plan && (
              <button className="btn btn-primary" onClick={() => void confirm()} disabled={saving || plan.imports.length === 0}>
                {saving ? 'Importing…' : `Import ${plan.imports.length} tank${plan.imports.length === 1 ? '' : 's'}`}
              </button>
            )}
          </>
        )
      }
    >
      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void readFile(f); e.target.value = ''; }}
      />

      {/* success state */}
      {done != null ? (
        <div className="state">
          <div className="big"><IconCheck size={16} /> Imported {done} tank{done === 1 ? '' : 's'}</div>
          <div className="muted">The Fuel Tank - S/N grid has been updated.</div>
        </div>
      ) : (
        <>
          {/* drop zone / picker */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInput.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current?.click(); } }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            style={{
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: '18px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--surface-2)',
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600 }}>{fileName || `${coarse ? 'Choose' : 'Choose or drop'} the Fuel Tank Serial Number .xlsx`}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Rows that name a vessel (Σκάφος) are matched by SAP or name and added as tanks. Stock rows and unknown vessels are skipped.
            </div>
          </div>

          {parsing && <LoadingState label="Reading workbook…" />}
          {parseError && <div className="state error"><div className="muted">{parseError}</div></div>}
          {saveError && <InlineError error={new Error(saveError)} onDismiss={() => setSaveError(null)} />}

          {plan && !parsing && (
            <>
              {isPhone ? (
                <p className="fti-stats">
                  <b>{plan.stats.toImport}</b> to import · {plan.stats.vesselsAffected} vessel{plan.stats.vesselsAffected === 1 ? '' : 's'}
                  {' · '}<span className={plan.stats.duplicates ? 'fti-warn' : undefined}>{plan.stats.duplicates} already present</span>
                  {' · '}<span className={plan.stats.unmatched ? 'fti-bad' : undefined}>{plan.stats.unmatched} unmatched</span>
                  {plan.stats.overflow > 0 && <>{' · '}<span className="fti-bad">{plan.stats.overflow} over 20 / vessel</span></>}
                </p>
              ) : (
                <div className="tile-grid" style={{ marginBottom: 14 }}>
                  <Tile value={plan.stats.toImport} label="To import" accent />
                  <Tile value={plan.stats.vesselsAffected} label="Vessels" />
                  <Tile value={plan.stats.duplicates} label="Already present" tone={plan.stats.duplicates ? 'warn' : undefined} />
                  <Tile value={plan.stats.unmatched} label="Unmatched" tone={plan.stats.unmatched ? 'bad' : undefined} />
                  {plan.stats.overflow > 0 && <Tile value={plan.stats.overflow} label="Over 20 / vessel" tone="bad" />}
                </div>
              )}

              {imports.length > 0 && (
                <>
                  <div className="field-label">Will be imported ({imports.length})</div>
                  {isPhone ? (
                    <div className="fti-list">
                      {imports.slice(0, 300).map((i, idx) => {
                        const sub = [
                          i.receiptDate && `Received ${df.maybe(i.receiptDate)}`,
                          i.installDate && `Installed ${df.maybe(i.installDate)}`,
                          i.floatInstalled && `Float ${i.floatInstalled}`,
                          i.note,
                        ].filter(Boolean).join(' · ');
                        return (
                          <div key={idx} className="fti-row">
                            <div>
                              <span className="fti-vsl">{i.vesselName}</span>
                              {i.matchedOn === 'name' && <> <Pill kind="info" title="Matched by vessel name, not SAP">name</Pill></>}
                              {' — '}<span className="fti-serial">{i.serial}</span>
                            </div>
                            {sub && <div className="muted fti-sub">{sub}</div>}
                          </div>
                        );
                      })}
                      {imports.length > 300 && <div className="muted fti-sub">+{imports.length - 300} more…</div>}
                    </div>
                  ) : (
                    <div className="tbl-wrap" style={{ maxHeight: 260, overflow: 'auto', marginBottom: 14 }}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>Vessel</th><th style={{ width: 120 }}>Serial</th>
                            <th style={{ width: 92 }}>Received</th><th style={{ width: 110 }}>Float</th>
                            <th style={{ width: 92 }}>Installed</th><th>Comment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {imports.slice(0, 300).map((i, idx) => (
                            <tr key={idx}>
                              <td style={cellSm}>
                                {i.vesselName}
                                {i.matchedOn === 'name' && (
                                  <span style={{ marginLeft: 4 }}><Pill kind="info" title="Matched by vessel name, not SAP">name</Pill></span>
                                )}
                              </td>
                              <td style={{ ...cellSm, fontFamily: 'var(--font-mono)' }}>{i.serial}</td>
                              <td style={cellSm} className="muted">{df.maybe(i.receiptDate)}</td>
                              <td style={cellSm} className="muted">{i.floatInstalled}</td>
                              <td style={cellSm} className="muted">{df.maybe(i.installDate)}</td>
                              <td style={cellSm} className="muted">{i.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {imports.length > 300 && <div className="muted" style={{ padding: 6, fontSize: 11 }}>+{imports.length - 300} more…</div>}
                    </div>
                  )}
                </>
              )}

              {unmatched.length > 0 && (
                <div style={{ border: '1px solid var(--warn-bd)', background: 'var(--warn-bg)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, color: 'var(--warn-fg)', marginBottom: 8 }}>
                    {unmatched.length} {unmatched.length === 1 ? 'vessel does' : 'vessels do'} not match Season {activeSeason}.
                  </div>
                  {scrapped ? (
                    <div className="muted" style={{ fontSize: 12 }}>
                      These will be scrapped (left out of the import).{' '}
                      <button className="btn btn-sm btn-ghost" onClick={() => setScrapped(false)}>Undo</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
                      <button className="btn btn-sm btn-danger" onClick={() => setScrapped(true)}>Scrap them</button>
                      {otherSeasons.length > 0 ? (
                        <>
                          <span className="muted">or move to season</span>
                          <select className="field" style={{ fontSize: 12 }} aria-label="Season to match against" value={moveSeason} onChange={(e) => setMoveSeason(e.target.value)}>
                            <option value="">Choose…</option>
                            {otherSeasons.map((s) => <option key={s} value={s}>Season {s}</option>)}
                          </select>
                          <button className="btn btn-sm btn-primary" disabled={!moveSeason || rechecking} onClick={() => void recheckSeason()}>
                            {rechecking ? 'Checking…' : 'Move & re-check'}
                          </button>
                        </>
                      ) : (
                        <span className="muted">No other seasons to try.</span>
                      )}
                    </div>
                  )}
                  {moveMsg && <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>{moveMsg}</div>}
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--warn-fg)' }}>Show unmatched vessels</summary>
                    <div className="tbl-wrap" style={isPhone ? { marginTop: 6 } : { maxHeight: 180, overflow: 'auto', marginTop: 6 }}>
                      <table className="tbl">
                        <thead><tr><th>Σκάφος</th><th style={{ width: 70 }}>Model</th><th style={{ width: 48 }}>S/N</th><th style={{ width: 120 }}>Serial</th></tr></thead>
                        <tbody>
                          {unmatched.map((s, idx) => (
                            <tr key={idx}>
                              <td style={cellSm}>{s.vessel}</td>
                              <td style={cellSm} className="muted">{s.model}</td>
                              <td style={cellSm}>{s.sn}</td>
                              <td style={{ ...cellSm, fontFamily: 'var(--font-mono)' }}>{s.serial}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )}

              {duplicates.length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }} className="muted">
                    {duplicates.length} already recorded - skipped to avoid duplicates
                  </summary>
                  <div className="tbl-wrap" style={isPhone ? { marginTop: 6 } : { maxHeight: 160, overflow: 'auto', marginTop: 6 }}>
                    <table className="tbl">
                      <thead><tr><th>Vessel</th><th style={{ width: 120 }}>Serial</th></tr></thead>
                      <tbody>
                        {duplicates.map((s, idx) => (
                          <tr key={idx}>
                            <td style={cellSm}>{s.vesselName ?? s.vessel}</td>
                            <td style={{ ...cellSm, fontFamily: 'var(--font-mono)' }}>{s.serial}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              {overflow.length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--err-fg)' }}>
                    {overflow.length} skipped - vessel already at the 20-tank limit
                  </summary>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    {overflow.map((s) => `${s.vesselName ?? s.vessel} · ${s.serial}`).join(', ')}
                  </div>
                </details>
              )}

              {plan.stats.rowsWithVessel === 0 && (
                <div className="muted">No vessel-bound tanks found in this workbook.</div>
              )}
              {plan.stats.rowsWithVessel > 0 && plan.imports.length === 0 && (
                <div className="muted">Nothing new to import - every vessel-bound tank is already recorded or unmatched.</div>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
}
